/**
 * Subscribes to the User Service SignalR hub for pending-approvals push.
 * When the server pushes PendingApprovalsUpdated, invalidates React Query cache so the UI refetches.
 * Use when the user has provider/office admin role so they see the notification bell.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import {
  LINKED_PROVIDERS_QUERY_KEY,
  PENDING_APPROVALS_SUMMARY_QUERY_KEY,
  PROVIDER_MAP_APPROVALS_QUERY_KEY,
} from '../constants/approvalsQueryKeys';
import { USER_API_CONFIG } from '../config/api.config';
import { getAuthScopes } from '../config/msal.config';
import { acquireTokenSilentDeduplicated } from '../utils/tokenAcquisition.utils';

const HUB_PATH = '/hubs/approvals';
const PENDING_APPROVALS_UPDATED = 'PendingApprovalsUpdated';

function getHubBaseUrl(): string {
  const configured = USER_API_CONFIG.baseUrl?.trim();
  if (configured) {
    try {
      return new URL(configured).origin;
    } catch {
      return configured;
    }
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

const MIN_PUSH_INTERVAL_MS = 3000;

export interface UseApprovalsPushOptions {
  /** When true, connect to the hub and subscribe. When false, disconnect. */
  enabled: boolean;
  /** Called when the server pushes PendingApprovalsUpdated. Throttled to at most once per 3s; if animation is running, the next run is queued. */
  onPushReceived?: () => void;
}

export interface UseApprovalsPushResult {
  /** True when the SignalR connection is in Connected state. */
  isConnected: boolean;
  /** Call when the notification animation has finished so the next queued push can run. */
  notifyAnimationComplete: () => void;
}

/**
 * Connects to the approvals hub when enabled and the user is authenticated.
 * On PendingApprovalsUpdated, invalidates pending-approvals-summary, provider-map-approvals, and linked-providers
 * so active observers refetch once (TanStack Query default); avoids duplicate refetch from invalidate + refetch.
 */
export function useApprovalsPush(options: UseApprovalsPushOptions): UseApprovalsPushResult {
  const { enabled, onPushReceived } = options;
  const queryClient = useQueryClient();
  const { instance, accounts } = useMsal();
  const accountId = accounts?.[0]?.homeAccountId ?? '';
  const account = accounts?.[0];
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);
  const onPushReceivedRef = useRef(onPushReceived);
  const lastPushAtRef = useRef<number>(0);
  const animationRunningRef = useRef<boolean>(false);
  const pendingPushRef = useRef<boolean>(false);

  onPushReceivedRef.current = onPushReceived;

  const tryNotify = useRef(() => {
    const now = Date.now();
    if (animationRunningRef.current) {
      pendingPushRef.current = true;
      return;
    }
    if (now - lastPushAtRef.current < MIN_PUSH_INTERVAL_MS) {
      pendingPushRef.current = true;
      return;
    }
    lastPushAtRef.current = now;
    animationRunningRef.current = true;
    onPushReceivedRef.current?.();
  }).current;

  const notifyAnimationComplete = useRef(() => {
    animationRunningRef.current = false;
    if (pendingPushRef.current) {
      pendingPushRef.current = false;
      const now = Date.now();
      if (now - lastPushAtRef.current >= MIN_PUSH_INTERVAL_MS) {
        lastPushAtRef.current = now;
        animationRunningRef.current = true;
        onPushReceivedRef.current?.();
      }
    }
  }).current;

  useEffect(() => {
    if (!enabled || !account) {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
        connectionRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    const baseUrl = getHubBaseUrl();
    if (!baseUrl) return;

    const hubUrl = baseUrl.replace(/\/+$/, '') + HUB_PATH;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const result = await acquireTokenSilentDeduplicated(instance, account, getAuthScopes());
          return result.accessToken;
        },
      })
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    connection.on(PENDING_APPROVALS_UPDATED, () => {
      queryClient.invalidateQueries({ queryKey: [PENDING_APPROVALS_SUMMARY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROVIDER_MAP_APPROVALS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [LINKED_PROVIDERS_QUERY_KEY] });
      tryNotify();
    });

    connection
      .start()
      .then(() => setIsConnected(true))
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        if (import.meta.env.DEV) {
          console.warn('[ApprovalsPush] Hub connection failed:', error);
        }
        import('../services/applicationInsights.service')
          .then(({ trackException }) => {
            trackException(error, undefined, {
              source: 'ApprovalsPush',
              event: 'HubConnectionFailed',
            });
          })
          .catch(() => {});
      });

    const onReconnected = () => setIsConnected(true);
    const onClosed = () => setIsConnected(false);
    connection.onreconnected(onReconnected);
    connection.onclose(onClosed);

    return () => {
      connection.off(PENDING_APPROVALS_UPDATED);
      // No offreconnected/offclose in SignalR JS client; stop() clears handlers
      connection
        .stop()
        .catch(() => {})
        .finally(() => {
          connectionRef.current = null;
          setIsConnected(false);
        });
    };
  }, [enabled, accountId, instance, queryClient]);

  return { isConnected, notifyAnimationComplete };
}
